import { useEffect, useState, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import useBreakpoint from '../hooks/useBreakpoint';

/* ── Design tokens ── */
const T = {
  ink:    '#111',
  muted:  '#888',
  border: '#e5e5e5',
  bg:     '#f7f7f7',
  font:   "'Helvetica Neue', Helvetica, Arial, sans-serif",
};

/* ── Status config ── */
const STATUS_OPTS  = ['offen','in_bearbeitung','erledigt','archiviert'];
const STATUS_LABEL = { offen:'Offen', in_bearbeitung:'In Bearbeitung', erledigt:'Erledigt', archiviert:'Archiviert' };
const STATUS_STYLE = {
  offen:          { background:'#fef3c7', color:'#92400e' },
  in_bearbeitung: { background:'#f5f5f5',  color:'#111' },
  erledigt:       { background:'#f0fdf4', color:'#166534' },
  archiviert:     { background:'#f9fafb', color:T.muted   },
};
const PRIORITY_DOT = { niedrig:'#9ca3af', mittel:'#f59e0b', hoch:'#ef4444' };

/* ── Helpers ── */
function fmtDate(d) { if (!d) return '–'; return new Date(d+'T00:00:00').toLocaleDateString('de-DE'); }
function isToday(d) { if (!d) return false; const t=new Date(); const dt=new Date(d+'T00:00:00'); return dt.toDateString()===t.toDateString(); }
function isPast(d)  { if (!d) return false; return new Date(d+'T00:00:00') < new Date(new Date().toDateString()); }
function vNum(n)    { return `V-${String(n||0).padStart(4,'0')}`; }

function Sk({ w='100%', h=14, r=8 }) {
  return <div className="skeleton" style={{ width:w, height:h, borderRadius:r }} />;
}

/* ── All Vorlagen (46) ── */
const VORLAGEN = [
  { id:'abmahnung-detailliert', title:'Abmahnungsrelevantes Verhalten (Detailliert)',
    description:'Abmahnung bei Fehlverhalten: Beschwerde aufnehmen, rechtssicher abmahnen und Verhalten überwachen.',
    todos:['Beschwerde aufnehmen (Lärmprotokoll, Fotos, Zeugenaussagen anfordern)','Mieter kontaktieren und zur Stellungnahme auffordern','Abmahnung rechtssicher formulieren (genaues Fehlverhalten, Datum, Uhrzeit)','Abmahnung per Einwurf-Einschreiben versenden','Verhalten monitoren → Bei Wiederholung: Kündigung prüfen'] },
  { id:'tierhaltung', title:'Anfrage Tierhaltung',
    description:'Anfrage zur Tierhaltung prüfen und genehmigen oder ablehnen.',
    todos:['Schriftliche Anfrage des Mieters entgegennehmen','Mietvertrag auf Tierhaltungsklausel prüfen','Art und Anzahl der Tiere klären','Entscheidung treffen und schriftlich mitteilen','Bei Genehmigung: Zusatzvereinbarung zum Mietvertrag erstellen'] },
  { id:'untervermietung', title:'Anfrage Untervermietung',
    description:'Anfrage zur Untervermietung prüfen und bearbeiten.',
    todos:['Schriftliche Anfrage des Mieters entgegennehmen','Mietvertrag auf Untervermietungsklausel prüfen','Bonität des Untermieters prüfen','Entscheidung schriftlich mitteilen','Bei Genehmigung: Untermietvertrag und Zusatzvereinbarung erstellen'] },
  { id:'besichtigung-detailliert', title:'Detailprüfung & Besichtigung (Detailliert)',
    description:'Besichtigung eines Mietobjekts vorbereiten, durchführen und dokumentieren.',
    todos:['Besichtigungstermin mit Interessenten vereinbaren','Unterlagen und Exposé vorbereiten','Besichtigung durchführen und Fragen beantworten','Feedback des Interessenten einholen','Besichtigungsprotokoll erstellen'] },
  { id:'kuendigung-eingang', title:'Eingang Kündigung (Detailliert)',
    description:'Eingegangene Kündigung des Mieters bearbeiten und Nachmietersuche einleiten.',
    todos:['Kündigung auf Formgültigkeit prüfen (Schriftform, Unterschrift, Frist)','Kündigungsfrist berechnen und im System eintragen','Eingangsbestätigung an Mieter senden','Nachmietersuche einleiten','Übergabetermin mit Mieter abstimmen'] },
  { id:'finanzierung-kauf', title:'Finanzierung & Kaufabwicklung',
    description:'Finanzierung für Immobilienkauf organisieren und Kaufabwicklung begleiten.',
    todos:['Finanzierungsangebote einholen und vergleichen','Bonitätsprüfung vorbereiten','Notartermin koordinieren','Kaufvertrag prüfen','Grundbucheintragung veranlassen'] },
  { id:'finanzierung-verhandlung', title:'Finanzierung & Verhandlung (Detailliert)',
    description:'Finanzierungsverhandlung mit Banken und Kaufpreisverhandlung führen.',
    todos:['Finanzierungsbedarf ermitteln','Banken anfragen und Angebote vergleichen','Zinsbindung und Tilgungsrate festlegen','Kaufpreisverhandlung mit Verkäufer führen','Finanzierungszusage einholen und Kaufvertrag abschließen'] },
  { id:'notar-closing', title:'Kaufabwicklung - Notar & Closing (Detailliert)',
    description:'Notartermin vorbereiten und Kaufabwicklung bis zur Schlüsselübergabe begleiten.',
    todos:['Notartermin vereinbaren und Unterlagen einreichen','Kaufvertragsentwurf prüfen','Notartermin wahrnehmen und Kaufvertrag unterzeichnen','Kaufpreiszahlung und Grunderwerbsteuer veranlassen','Schlüsselübergabe und Eigentumsübergang dokumentieren'] },
  { id:'kaution-abrechnung', title:'Kautionsabrechnung nach Übergabe (Detailliert)',
    description:'Kaution nach Mietende abrechnen und ggf. Einbehalte begründen.',
    todos:['Wohnungsübergabeprotokoll auswerten','Schäden über Normalverschleiß dokumentieren','Offene Miete und Nebenkosten prüfen','Kautionsabrechnung erstellen','Restbetrag auszahlen oder Einbehalt schriftlich begründen'] },
  { id:'kuendigung-vorbereitung', title:'Kündigung & Vorbereitung',
    description:'Kündigung vorbereiten und rechtssicher aussprechen.',
    todos:['Kündigungsgrund prüfen (Eigenbedarf, Verzug, etc.)','Kündigungsfrist berechnen','Kündigung schriftlich formulieren','Kündigung per Einwurf-Einschreiben zusenden','Kündigungseingang dokumentieren'] },
  { id:'eigenbedarf', title:'Kündigung wegen Eigenbedarf (Detailliert)',
    description:'Eigenbedarfskündigung rechtssicher vorbereiten und durchführen.',
    todos:['Eigenbedarf rechtssicher begründen (Verwandtschaft, konkreter Bedarf)','Soziale Härtefallprüfung vornehmen','Kündigung mit vollständiger Begründung schriftlich aufsetzen','Kündigung per Einwurf-Einschreiben versenden','Räumungsfrist überwachen und ggf. Nachmieter suchen'] },
  { id:'mahnwesen', title:'Mahnwesen & Abmahnung',
    description:'Offene Mietzahlungen mahnen und Abmahnung aussprechen.',
    todos:['Mietrückstand prüfen und dokumentieren','Erste Zahlungserinnerung per E-Mail senden','Erste Mahnung mit Fristsetzung versenden','Zweite Mahnung und Androhung weiterer Schritte','Rechtlichen Rat einholen bei weiterem Verzug'] },
  { id:'mietbeginn', title:'Mietbeginn / Onboarding (Detailliert)',
    description:'Neuen Mieter vollständig einweisen und Mietbeginn dokumentieren.',
    todos:['Mietvertrag unterzeichnen und Exemplar aushändigen','Kautionszahlung prüfen','Wohnungsübergabe mit Protokoll durchführen','Zählerstände dokumentieren und an Versorger melden','Mieter über Hausordnung und Notfallkontakte informieren'] },
  { id:'mieterhoehung', title:'Mieterhöhung',
    description:'Mieterhöhung ankündigen und rechtssicher umsetzen.',
    todos:['Ortsübliche Vergleichsmiete prüfen','Mieterhöhung berechnen (max. Kappungsgrenze beachten)','Mieterhöhungsschreiben erstellen','Schreiben mit Begründung und Belegen versenden','Zustimmung des Mieters abwarten und dokumentieren'] },
  { id:'mieterhoehung-detailliert', title:'Mieterhöhung durchführen (Detailliert)',
    description:'Vollständigen Mieterhöhungsprozess inkl. Mietspiegel und Zustimmung durchführen.',
    todos:['Mietspiegel oder Gutachten einholen','Kappungsgrenze und Wartefrist prüfen','Begründetes Mieterhöhungsschreiben aufsetzen','Schreiben nachweisbar zustellen','Zustimmungsfrist überwachen','Bei Verweigerung: Rechtliche Schritte prüfen'] },
  { id:'mieterwechsel', title:'Mieterwechsel ohne Kündigung (Detailliert)',
    description:'Mieterparteiwechsel bei laufendem Mietverhältnis rechtssicher umsetzen.',
    todos:['Anfrage des Mieters entgegennehmen','Bonität des neuen Mieters prüfen','Bestehenden Mietvertrag prüfen','Nachtrag zum Mietvertrag erstellen','Neue Mietpartei im System anlegen und informieren'] },
  { id:'mietsrueckstand', title:'Mietrückstand / Rechtsfall (Detailliert)',
    description:'Mietrückstand konsequent einfordern und rechtliche Schritte einleiten.',
    todos:['Rückstand dokumentieren und Zahlungshistorie zusammenstellen','Zahlungserinnerung und Mahnungen versenden','Anwalt einschalten','Kündigung wegen Zahlungsverzug prüfen','Räumungsklage einreichen falls nötig'] },
  { id:'nk-rueckfrage', title:'Nebenkosten-Rückfrage / Widerspruch',
    description:'Widerspruch des Mieters gegen Nebenkostenabrechnung prüfen.',
    todos:['Widerspruch des Mieters entgegennehmen','Abrechnungsbelege zusammenstellen','Berechnung nochmals prüfen','Schriftliche Stellungnahme an Mieter senden','Bei Berechtigung: Korrigierte Abrechnung erstellen'] },
  { id:'nk-klaeren', title:'Nebenkostenabrechnung klären (Detailliert)',
    description:'Streitige Nebenkostenabrechnung vollständig klären.',
    todos:['Alle Belege sammeln und sortieren','Abrechnung Position für Position prüfen','Ggf. Fehler korrigieren','Stellungnahme verfassen','Bei Einigung: Angepasste Zahlung vereinbaren'] },
  { id:'neue-immobilie', title:'Neue Immobilie anlegen (Detailliert)',
    description:'Neue Immobilie vollständig im System erfassen und verwalten.',
    todos:['Objektdaten vollständig erfassen','Grundriss und Fotos hochladen','Mieter und Einheiten anlegen','Mietverträge einscannen und hinterlegen','Versicherungen und Dienstleister eintragen'] },
  { id:'neuvermietung', title:'Neuvermietung',
    description:'Freie Einheit neu vermieten.',
    todos:['Exposé erstellen und Fotos machen','Inserat auf Portalen schalten','Besichtigungen koordinieren','Bewerber auswählen und Bonität prüfen','Mietvertrag erstellen und unterzeichnen'] },
  { id:'objektakquise', title:'Objektakquise - Sourcing & Erstprüfung (Detailliert)',
    description:'Potentielle Kaufobjekte identifizieren und erstmalig prüfen.',
    todos:['Suchprofile festlegen','Potentielle Objekte identifizieren','Lage und Mikrolage analysieren','Erstbesichtigung durchführen','Kaufpreisindikation ermitteln'] },
  { id:'onboarding-objekt', title:'Onboarding neues Objekt',
    description:'Neu erworbenes Objekt in die Verwaltung aufnehmen.',
    todos:['Objektdaten anlegen','Bestehende Mieter und Verträge übernehmen','Versicherungen überprüfen','Zählerstände erfassen','Dienstleister und Handwerker hinterlegen'] },
  { id:'ratenzahlung', title:'Ratenzahlung',
    description:'Ratenzahlungsvereinbarung mit Mieter treffen.',
    todos:['Rückstand ermitteln','Ratenzahlungsplan besprechen','Schriftliche Vereinbarung erstellen','Raten überwachen','Bei Nichtzahlung: Vereinbarung widerrufen'] },
  { id:'ratenzahlung-detailliert', title:'Ratenzahlungsvereinbarung (Detailliert)',
    description:'Vollständige Ratenzahlungsvereinbarung aufsetzen und überwachen.',
    todos:['Gesamtrückstand dokumentieren','Realistischen Ratenplan erstellen','Rechtlich geprüfte Vereinbarung aufsetzen','Vereinbarung beidseitig unterzeichnen','Ratenzahlungen monatlich kontrollieren','Bei Ausfall: Kündigung prüfen'] },
  { id:'sanierung', title:'Sanierung / Modernisierung',
    description:'Sanierungsmaßnahme planen und koordinieren.',
    todos:['Sanierungsbedarf feststellen','Angebote von Handwerkern einholen','Mieter über Maßnahmen informieren','Arbeiten koordinieren und überwachen','Abnahme durchführen und dokumentieren'] },
  { id:'sanierung-detailliert', title:'Sanierung / Modernisierung (Detailliert)',
    description:'Umfangreiche Sanierungs- oder Modernisierungsmaßnahme vollständig abwickeln.',
    todos:['Sanierungsbedarf und -umfang festlegen','Genehmigungen prüfen und einholen','Mehrere Angebote einholen und vergleichen','Mieter mit Ankündigungsfrist informieren','Mietminderungsansprüche prüfen','Baufortschritt kontrollieren','Abnahme und Mängelrügen bearbeiten','Kosten abrechnen und Mieterhöhung prüfen'] },
  { id:'schadensmanagement', title:'Schadensmanagement',
    description:'Schaden am Objekt aufnehmen und beheben.',
    todos:['Schaden dokumentieren (Fotos, Beschreibung)','Ursache klären','Handwerker beauftragen','Versicherung informieren falls relevant','Reparatur überwachen und Abnahme durchführen'] },
  { id:'schadensmeldung', title:'Schadensmeldung Mieter (Detailliert)',
    description:'Schadensmeldung des Mieters vollständig bearbeiten.',
    todos:['Schadensmeldung des Mieters entgegennehmen','Schaden besichtigen und dokumentieren','Verantwortlichkeit klären (Mieter / Vermieter / Versicherung)','Reparatur beauftragen','Abnahme durchführen und Kosten klären'] },
  { id:'sonderuendigung', title:'Sonderkündigung (Eigenbedarf / Todesfall)',
    description:'Sonderkündigung aus besonderem Anlass vorbereiten und ausführen.',
    todos:['Kündigungsgrund prüfen','Fristen und Formvorschriften beachten','Kündigung formulieren und begründen','Nachweisbar zustellen','Rückabwicklung und Übergabe koordinieren'] },
  { id:'sourcing', title:'Sourcing & Due Diligence',
    description:'Immobilienkauf vorbereiten durch Marktanalyse und Due Diligence.',
    todos:['Marktanalyse durchführen','Objektunterlagen anfordern','Technische Due Diligence (Zustand, Mängel)','Rechtliche Due Diligence (Lasten, Grundbuch)','Kaufentscheidung treffen'] },
  { id:'todesfall', title:'Todesfall Mieter',
    description:'Todesfall eines Mieters bearbeiten und Mietverhältnis klären.',
    todos:['Todesfall dokumentieren','Erben oder Nachlasspfleger kontaktieren','Mietverhältnis klären (Übernahme oder Kündigung)','Wohnungsauflösung koordinieren','Zählerstände ablesen und Übergabe durchführen'] },
  { id:'wartung-pruefung', title:'Turnusmäßige Prüfung / Wartung (Detailliert)',
    description:'Regelmäßige Wartungs- und Prüftermine koordinieren und dokumentieren.',
    todos:['Wartungsplan erstellen (Heizung, Rauchmelder, Aufzug etc.)','Termine mit Dienstleistern vereinbaren','Wartungsarbeiten überwachen','Protokolle und Zertifikate ablegen','Nächsten Wartungstermin eintragen'] },
  { id:'versicherungsfall', title:'Versicherungsfall (Detailliert)',
    description:'Versicherungsfall melden, bearbeiten und Schadensersatz durchsetzen.',
    todos:['Schaden dokumentieren (Fotos, Gutachter)','Versicherung fristgerecht informieren','Schadensmeldung einreichen','Gutachtertermin koordinieren','Schadensregulierung überwachen','Reparatur nach Freigabe beauftragen'] },
  { id:'versorgerwechsel', title:'Versorgerwechsel & Zähler',
    description:'Versorgerwechsel koordinieren und Zählerstände übergeben.',
    todos:['Kündigung beim alten Versorger einreichen','Neuen Versorger auswählen und Vertrag abschließen','Zählerstände bei Übergabe dokumentieren','Neuen Versorger über Mieterwechsel informieren','Abschlussrechnungen prüfen'] },
  { id:'vertragsaenderungen', title:'Vertragsänderungen',
    description:'Änderungen am Mietvertrag rechtssicher umsetzen.',
    todos:['Änderungsbedarf klären','Nachtrag zum Mietvertrag erstellen','Nachtrag beidseitig unterzeichnen','Nachtrag im System hinterlegen','Mieter über Änderungen informieren'] },
  { id:'vor-uebergabe', title:'Vor Übergabetermin',
    description:'Wohnungsübergabe vorbereiten.',
    todos:['Übergabetermin mit Mieter vereinbaren','Übergabeprotokoll vorbereiten','Zählerstände notieren','Schlüssel zusammenstellen','Schäden vorher dokumentieren'] },
  { id:'wartung-versicherung', title:'Wartung & Versicherungsfälle',
    description:'Wartungsmaßnahmen und Versicherungsfälle koordinieren.',
    todos:['Wartungsbedarf und Versicherungsfälle identifizieren','Dienstleister und Versicherung kontaktieren','Termine koordinieren','Arbeiten überwachen','Protokolle und Unterlagen ablegen'] },
  { id:'weitervermietung-kein-nm', title:'Weitervermietung - Kein Nachmieter (Detailliert)',
    description:'Weitervermietung ohne bestehenden Nachmieter aktiv angehen.',
    todos:['Exposé aktualisieren','Portale schalten und Inserat optimieren','Besichtigungen koordinieren','Interessenten qualifizieren','Mietvertrag vorbereiten'] },
  { id:'weitervermietung-nm', title:'Weitervermietung - Nachmieter vorhanden (Detailliert)',
    description:'Weitervermietung mit bereits bekanntem Nachmieter abwickeln.',
    todos:['Bonität des Nachmieters prüfen','Neuen Mietvertrag erstellen','Übergabe mit altem Mieter durchführen','Neues Mietverhältnis starten','Zählerstände und Kautionswechsel dokumentieren'] },
  { id:'entruempelung', title:'Wohnung entrümpelungsbedürftig',
    description:'Entrümpelungsbedürftige Wohnung abwickeln.',
    todos:['Ausmaß der Entrümpelung dokumentieren','Kostentragung klären (Mieter / Vermieter / Erbmasse)','Entrümpelungsunternehmen beauftragen','Entsorgungsnachweise einholen','Wohnung nach Entrümpelung begehren'] },
  { id:'wohnungsuebergabe', title:'Wohnungsübergabe / -abnahme (Detailliert)',
    description:'Wohnungsübergabe vollständig und rechtssicher durchführen.',
    todos:['Übergabeprotokoll vorbereiten','Gemeinsame Begehung mit Mieter durchführen','Schäden und Mängel dokumentieren','Zählerstände ablesen','Alle Schlüssel entgegennehmen','Kaution prüfen und ggf. einbehalten'] },
  { id:'zwangsraeumung', title:'Zwangsräumung',
    description:'Zwangsräumung einleiten und begleiten.',
    todos:['Vollstreckbaren Räumungstitel einholen','Gerichtsvollzieher beauftragen','Räumungstermin koordinieren','Wohnung sichern und dokumentieren','Schloss austauschen'] },
  { id:'zwangsraeumung-detailliert', title:'Zwangsräumung (Detailliert)',
    description:'Vollständigen Zwangsräumungsprozess rechtssicher abwickeln.',
    todos:['Mietrückstand und Kündigungsgrund dokumentieren','Anwalt einschalten','Räumungsklage einreichen','Urteil und Vollstreckungstitel erwirken','Gerichtsvollzieher beauftragen','Räumung durchführen und dokumentieren','Wohnung absichern und neu vermieten'] },
  { id:'zaehlerstand', title:'Zählerstand / Versorgerwechsel (Detailliert)',
    description:'Zählerstände erfassen und Versorgerwechsel vollständig abwickeln.',
    todos:['Alle Zählerstände erfassen (Strom, Gas, Wasser)','Ablesedaten an Versorger melden','Kündigung bei bisherigem Versorger prüfen','Neuen Versorger anmelden','Nachzahlungen oder Erstattungen prüfen'] },
  { id:'uebergabe-onboarding', title:'Übergabe & Onboarding',
    description:'Wohnungsübergabe und Mieter-Onboarding kombiniert durchführen.',
    todos:['Übergabeprotokoll erstellen','Schlüsselübergabe dokumentieren','Mieter in Hausregeln einweisen','Notfallkontakte und Ansprechpartner übergeben','Mietbeginn im System eintragen'] },
];

/* ══════════════════════════════════════════════════════
   Template Selector Modal (Schritt 1)
══════════════════════════════════════════════════════ */
function TemplateModal({ onClose, onSelect, onWithout }) {
  const [search, setSearch]   = useState('');
  const [expanded, setExpanded] = useState(null);
  const [selected, setSelected] = useState(null);

  const filtered = VORLAGEN.filter(v =>
    !search || v.title.toLowerCase().includes(search.toLowerCase())
  );

  function handleWeiter() {
    if (selected) onSelect(selected);
  }

  return (
    <div
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position:'fixed', inset:0, zIndex:100,
        background:'rgba(17,24,39,0.5)', backdropFilter:'blur(3px)',
        display:'flex', alignItems:'center', justifyContent:'center', padding:'20px',
      }}
    >
      <div style={{
        background:'white', borderRadius:16, width:'100%', maxWidth:640,
        boxShadow:'0 24px 64px rgba(0,0,0,0.16)', display:'flex', flexDirection:'column',
        maxHeight:'85vh',
      }}>
        {/* Header */}
        <div style={{ padding:'22px 24px 16px', borderBottom:`1px solid ${T.border}`, display:'flex', justifyContent:'space-between', alignItems:'center', flexShrink:0 }}>
          <h2 style={{ margin:0, fontSize:17, fontWeight:800, color:T.ink }}>Neuen Vorgang erstellen</h2>
          <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', color:T.muted, padding:4, display:'flex', borderRadius:6 }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        {/* Search */}
        <div style={{ padding:'12px 24px 8px', flexShrink:0 }}>
          <div style={{ position:'relative' }}>
            <svg style={{ position:'absolute', left:11, top:'50%', transform:'translateY(-50%)', pointerEvents:'none' }} width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={T.muted} strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            <input
              autoFocus value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Suchen…"
              style={{ width:'100%', boxSizing:'border-box', border:`1.5px solid ${T.border}`, borderRadius:8, padding:'8px 12px 8px 30px', fontSize:13, fontFamily:T.font, color:T.ink, outline:'none' }}
              onFocus={e => e.target.style.borderColor = T.ink}
              onBlur={e => e.target.style.borderColor = T.border}
            />
          </div>
        </div>

        {/* Template list */}
        <div style={{ flex:1, overflowY:'auto', padding:'4px 24px 8px' }}>
          {filtered.map(v => {
            const isExp = expanded === v.id;
            const isSel = selected?.id === v.id;
            return (
              <div key={v.id} style={{ marginBottom:6 }}>
                <div
                  onClick={() => { setSelected(v); setExpanded(isExp ? null : v.id); }}
                  style={{
                    display:'flex', alignItems:'center', gap:12, padding:'12px 14px',
                    border:`1.5px solid ${isSel ? T.ink : T.border}`,
                    borderRadius:10, cursor:'pointer', background:isSel ? '#f5f5f5' : 'white',
                    transition:'border-color 0.15s, background 0.15s',
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={isSel ? T.ink : T.muted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
                  </svg>
                  <span style={{ flex:1, fontSize:13, fontWeight:isSel?700:500, color:isSel?T.ink:T.ink }}>
                    {v.title}
                  </span>
                  <svg style={{ transform:`rotate(${isExp?'180deg':'0deg'})`, transition:'transform 0.2s' }} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.muted} strokeWidth="2" strokeLinecap="round"><polyline points="6 9 12 15 18 9"/></svg>
                </div>
                {isExp && (
                  <div style={{ background:T.bg, border:`1px solid ${T.border}`, borderTop:'none', borderRadius:'0 0 10px 10px', padding:'10px 14px 12px 42px' }}>
                    <div style={{ fontSize:12, color:T.muted, marginBottom:8, lineHeight:1.5 }}>{v.description}</div>
                    {v.todos.map((t,i) => (
                      <div key={i} style={{ display:'flex', alignItems:'flex-start', gap:8, marginBottom:5 }}>
                        <div style={{ width:16, height:16, borderRadius:'50%', border:`1.5px solid ${T.border}`, flexShrink:0, marginTop:1 }} />
                        <span style={{ fontSize:12, color:T.ink }}>{t}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          {filtered.length === 0 && (
            <div style={{ textAlign:'center', padding:'32px 0', fontSize:13, color:T.muted }}>Keine Vorlagen gefunden.</div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding:'14px 24px 20px', borderTop:`1px solid ${T.border}`, display:'flex', justifyContent:'space-between', alignItems:'center', flexShrink:0 }}>
          <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', fontSize:13, fontWeight:600, color:T.ink, fontFamily:T.font, padding:0 }}>
            Abbrechen
          </button>
          <div style={{ display:'flex', gap:10 }}>
            <button onClick={onWithout} style={{ padding:'9px 16px', borderRadius:8, border:`1.5px solid ${T.border}`, background:'white', color:T.ink, fontSize:13, fontWeight:600, cursor:'pointer', fontFamily:T.font }}>
              Ohne Vorlage fortfahren
            </button>
            <button
              onClick={handleWeiter} disabled={!selected}
              style={{ padding:'9px 20px', borderRadius:8, border:'none', background:selected?T.ink:'#d1d5db', color:'white', fontSize:13, fontWeight:700, cursor:selected?'pointer':'not-allowed', fontFamily:T.font }}
            >
              Weiter
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════
   Vorgang Detail View (Schritt 2 / Ansicht)
══════════════════════════════════════════════════════ */
function VorgangDetail({ vorgang, todos: initialTodos, vNum: number, profile, onClose, onSaved, isNew, template }) {
  const { user } = useAuth();
  const { addToast } = useToast();
  const [activeTab, setActiveTab] = useState('todos');
  const [title, setTitle]         = useState(vorgang?.title || template?.title || '');
  const [description, setDescription] = useState(vorgang?.description || template?.description || '');
  const [status, setStatus]       = useState(vorgang?.status || 'offen');
  const [todos, setTodos]         = useState(initialTodos || []);
  const [newTodo, setNewTodo]     = useState('');
  const [onlyOpen, setOnlyOpen]   = useState(false);
  const [saving, setSaving]       = useState(false);

  const shownTodos = onlyOpen ? todos.filter(t => !t.done) : todos;

  async function toggleTodo(todo) {
    if (isNew) {
      setTodos(prev => prev.map(t => t.id === todo.id ? { ...t, done: !t.done } : t));
      return;
    }
    const { error } = await supabase.from('vorgang_todos').update({ done: !todo.done }).eq('id', todo.id).eq('owner_id', user.id);
    if (!error) setTodos(prev => prev.map(t => t.id === todo.id ? { ...t, done: !t.done } : t));
  }

  async function addTodo() {
    if (!newTodo.trim()) return;
    if (isNew) {
      setTodos(prev => [...prev, { id: Date.now().toString(), title: newTodo.trim(), done: false }]);
    } else {
      const { data, error } = await supabase.from('vorgang_todos').insert({
        vorgang_id: vorgang.id, owner_id: user.id,
        title: newTodo.trim(), sort_order: todos.length,
      }).select().single();
      if (!error) setTodos(prev => [...prev, data]);
    }
    setNewTodo('');
  }

  async function deleteTodo(id) {
    if (isNew) { setTodos(prev => prev.filter(t => t.id !== id)); return; }
    await supabase.from('vorgang_todos').delete().eq('id', id).eq('owner_id', user.id);
    setTodos(prev => prev.filter(t => t.id !== id));
  }

  async function handleSave() {
    setSaving(true);
    if (isNew) {
      const count = await supabase.from('vorgaenge').select('id', { count:'exact', head:true }).eq('owner_id', user.id);
      const nextNum = (count.count || 0) + 1;
      const { data: v, error } = await supabase.from('vorgaenge').insert({
        owner_id: user.id, title, description, status, number: nextNum,
        priority: 'mittel',
      }).select().single();
      if (error) { addToast('Fehler beim Speichern.', 'error'); setSaving(false); return; }
      if (todos.length > 0) {
        await supabase.from('vorgang_todos').insert(
          todos.map((t, i) => ({ vorgang_id: v.id, owner_id: user.id, title: t.title, done: t.done, sort_order: i }))
        );
      }
      addToast('Vorgang erstellt.', 'success');
    } else {
      const { error } = await supabase.from('vorgaenge').update({ title, description, status, updated_at: new Date().toISOString() }).eq('id', vorgang.id).eq('owner_id', user.id);
      if (error) { addToast('Fehler beim Speichern.', 'error'); setSaving(false); return; }
      addToast('Vorgang gespeichert.', 'success');
    }
    setSaving(false);
    onSaved();
  }

  const TABS = [{ key:'todos', label:'To-Dos' }, { key:'verlauf', label:'Verlauf' }, { key:'kommentare', label:'Kommentare' }];

  return (
    <div style={{ maxWidth:860, fontFamily:T.font, color:T.ink }}>
      {/* Breadcrumb */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
        <div style={{ display:'flex', alignItems:'center', gap:6, fontSize:13, color:T.muted }}>
          <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', color:T.ink, fontSize:13, fontWeight:600, fontFamily:T.font, padding:0 }}>Vorgänge</button>
          <span>›</span>
          <span style={{ color:T.ink, fontWeight:600 }}>{number}</span>
        </div>
        <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', color:T.muted, display:'flex', padding:4, borderRadius:6 }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
      </div>

      {/* Vorgang Card */}
      <div style={{ background:'white', border:`1.5px solid ${T.border}`, borderRadius:14, padding:'20px 24px', marginBottom:0, boxShadow:'0 2px 8px rgba(0,0,0,0.04)' }}>
        <div style={{ display:'flex', gap:14, marginBottom:12 }}>
          {/* Status circle */}
          <button
            onClick={() => setStatus(s => s === 'erledigt' ? 'offen' : 'erledigt')}
            style={{ width:24, height:24, borderRadius:'50%', border:`2px solid ${status==='erledigt'?'#16a34a':T.border}`, background:status==='erledigt'?'#16a34a':'white', flexShrink:0, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', marginTop:2 }}
          >
            {status==='erledigt' && <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>}
          </button>
          <div style={{ flex:1 }}>
            <div style={{ display:'flex', alignItems:'baseline', gap:10, marginBottom:4 }}>
              <span style={{ fontSize:13, fontWeight:700, color:T.muted }}>{number}</span>
              <input
                value={title} onChange={e => setTitle(e.target.value)}
                style={{ flex:1, border:'none', outline:'none', fontSize:17, fontWeight:800, color:T.ink, fontFamily:T.font, background:'transparent', letterSpacing:'-0.3px' }}
              />
            </div>
            {template && <div style={{ fontSize:12, color:T.muted, marginBottom:6 }}>{template.title}</div>}
            <textarea
              value={description} onChange={e => setDescription(e.target.value)}
              placeholder="Beschreibung hinzufügen…"
              rows={2}
              style={{ width:'100%', border:'none', outline:'none', fontSize:13, color:T.muted, fontFamily:T.font, background:'transparent', resize:'none', lineHeight:1.5, boxSizing:'border-box' }}
            />
            <div style={{ display:'flex', alignItems:'center', gap:6, marginTop:4 }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={T.muted} strokeWidth="2" strokeLinecap="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
              <span style={{ fontSize:12, color:T.muted }}>Verantwortlicher: {profile?.full_name || profile?.first_name || 'Ich'}</span>
            </div>
          </div>
        </div>

        {/* Status selector row */}
        <div style={{ display:'flex', gap:6, flexWrap:'wrap', paddingLeft:38, marginBottom:14 }}>
          {STATUS_OPTS.map(s => (
            <button key={s} onClick={() => setStatus(s)} style={{
              padding:'3px 10px', borderRadius:99, border:`1.5px solid ${status===s?T.ink:T.border}`,
              background:status===s?'#f5f5f5':'white', color:status===s?T.ink:T.muted,
              fontSize:11, fontWeight:status===s?700:400, cursor:'pointer', fontFamily:T.font,
            }}>
              {STATUS_LABEL[s]}
            </button>
          ))}
        </div>

        {/* Actions */}
        <div style={{ display:'flex', justifyContent:'flex-end', gap:10, paddingTop:12, borderTop:`1px solid ${T.border}` }}>
          <button onClick={onClose} style={{ padding:'8px 16px', borderRadius:8, border:`1.5px solid ${T.border}`, background:'white', color:T.ink, fontSize:13, fontWeight:600, cursor:'pointer', fontFamily:T.font }}>
            Abbrechen
          </button>
          <button onClick={handleSave} disabled={saving} style={{ padding:'8px 20px', borderRadius:8, border:'none', background:saving?T.muted:T.ink, color:'white', fontSize:13, fontWeight:700, cursor:saving?'not-allowed':'pointer', fontFamily:T.font }}>
            {saving ? 'Speichern…' : 'Speichern'}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display:'flex', gap:0, borderBottom:`1px solid ${T.border}`, marginTop:20 }}>
        {TABS.map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{
            padding:'10px 18px', border:'none', background:'none', cursor:'pointer',
            fontSize:13, fontWeight:activeTab===tab.key?700:400,
            color:activeTab===tab.key?T.ink:T.muted, fontFamily:T.font,
            borderBottom:`2px solid ${activeTab===tab.key?T.ink:'transparent'}`,
            marginBottom:-1,
          }}>
            {tab.label}
            {tab.key==='todos' && todos.length>0 && (
              <span style={{ marginLeft:6, background:T.border, color:T.muted, borderRadius:99, padding:'1px 6px', fontSize:10, fontWeight:700 }}>
                {todos.filter(t=>!t.done).length}/{todos.length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* To-Dos tab */}
      {activeTab==='todos' && (
        <div style={{ marginTop:16 }}>
          {/* Toggle */}
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
            <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', fontSize:12, color:T.muted }}>
              <div
                onClick={() => setOnlyOpen(v => !v)}
                style={{ width:34, height:18, borderRadius:99, background:onlyOpen?T.ink:'#d1d5db', position:'relative', cursor:'pointer', transition:'background 0.2s' }}
              >
                <div style={{ position:'absolute', top:2, left:onlyOpen?18:2, width:14, height:14, borderRadius:'50%', background:'white', transition:'left 0.2s' }} />
              </div>
              Nur offene To-Dos anzeigen
            </label>
          </div>

          {/* Todo list */}
          <div style={{ background:'white', border:`1.5px solid ${T.border}`, borderRadius:12, overflow:'hidden' }}>
            {shownTodos.length === 0 ? (
              <div style={{ padding:'24px', textAlign:'center', fontSize:13, color:T.muted }}>
                {onlyOpen ? 'Alle To-Dos erledigt!' : 'Noch keine To-Dos vorhanden.'}
              </div>
            ) : shownTodos.map((todo, i) => (
              <div key={todo.id} style={{ display:'flex', alignItems:'center', gap:12, padding:'13px 16px', borderBottom: i<shownTodos.length-1?`1px solid ${T.border}`:'none' }}>
                <button
                  onClick={() => toggleTodo(todo)}
                  style={{ width:20, height:20, borderRadius:'50%', border:`2px solid ${todo.done?'#16a34a':T.border}`, background:todo.done?'#16a34a':'white', flexShrink:0, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}
                >
                  {todo.done && <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>}
                </button>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.muted} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                <span style={{ flex:1, fontSize:13, color:todo.done?T.muted:T.ink, textDecoration:todo.done?'line-through':'none' }}>
                  {todo.title}
                </span>
                <button onClick={() => deleteTodo(todo.id)} style={{ background:'none', border:'none', cursor:'pointer', color:'#d1d5db', padding:2, display:'flex', borderRadius:4 }}
                  onMouseEnter={e => e.currentTarget.style.color='#ef4444'}
                  onMouseLeave={e => e.currentTarget.style.color='#d1d5db'}
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
              </div>
            ))}

            {/* Add todo */}
            <div style={{ borderTop:`1px solid ${T.border}`, padding:'0' }}>
              <div style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 16px' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.ink} strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                <input
                  value={newTodo} onChange={e => setNewTodo(e.target.value)}
                  onKeyDown={e => { if(e.key==='Enter') { e.preventDefault(); addTodo(); } }}
                  placeholder="Neues To-Do erstellen"
                  style={{ flex:1, border:'none', outline:'none', fontSize:13, color:T.ink, fontFamily:T.font, background:'transparent' }}
                />
                {newTodo.trim() && (
                  <button onClick={addTodo} style={{ background:T.ink, border:'none', color:'white', borderRadius:6, padding:'4px 10px', fontSize:12, fontWeight:600, cursor:'pointer', fontFamily:T.font }}>
                    + Hinzufügen
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab==='verlauf' && (
        <div style={{ marginTop:16, background:'white', border:`1.5px solid ${T.border}`, borderRadius:12, padding:'32px', textAlign:'center' }}>
          <div style={{ fontSize:13, color:T.muted }}>Noch keine Einträge im Verlauf.</div>
        </div>
      )}
      {activeTab==='kommentare' && (
        <div style={{ marginTop:16, background:'white', border:`1.5px solid ${T.border}`, borderRadius:12, padding:'32px', textAlign:'center' }}>
          <div style={{ fontSize:13, color:T.muted }}>Noch keine Kommentare vorhanden.</div>
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════
   Main Page
══════════════════════════════════════════════════════ */
export default function Vorgaenge() {
  const { user, profile } = useAuth();
  const { addToast }      = useToast();
  const { isMobile }      = useBreakpoint();

  const [tab, setTab]           = useState('vorgaenge');
  const [vorgaenge, setVorgaenge] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const [statusFilter, setStatusFilter] = useState(null);
  const [templateModal, setTemplateModal] = useState(false);
  const [detail, setDetail]     = useState(null);

  useEffect(() => { fetchVorgaenge(); }, []);

  async function fetchVorgaenge() {
    const { data } = await supabase
      .from('vorgaenge')
      .select('*, tenants(first_name,last_name), properties(name), vorgang_todos(*)')
      .eq('owner_id', user.id)
      .order('created_at', { ascending: false });
    setVorgaenge(data || []);
    setLoading(false);
  }

  async function handleDelete(id) {
    if (!window.confirm('Vorgang wirklich löschen?')) return;
    const { error } = await supabase.from('vorgaenge').delete().eq('id', id).eq('owner_id', user.id);
    if (error) { addToast('Fehler beim Löschen.', 'error'); }
    else { setVorgaenge(prev => prev.filter(v => v.id !== id)); addToast('Vorgang gelöscht.', 'success'); }
  }

  function handleSaved() {
    setDetail(null);
    setTemplateModal(false);
    fetchVorgaenge();
  }

  /* ── KPIs ── */
  const aktive       = vorgaenge.filter(v => v.status==='offen'||v.status==='in_bearbeitung');
  const ueberfaellig = aktive.filter(v => v.due_date && isPast(v.due_date));
  const faelligHeute = aktive.filter(v => v.due_date && isToday(v.due_date));

  /* ── Filtered lists ── */
  const archiviert = vorgaenge.filter(v => v.status==='archiviert');
  const mainList   = vorgaenge.filter(v => v.status!=='archiviert');

  function applyFilters(list) {
    return list.filter(v => {
      const matchStatus = !statusFilter || v.status===statusFilter;
      const q = search.toLowerCase();
      const matchSearch = !q || v.title?.toLowerCase().includes(q);
      return matchStatus && matchSearch;
    });
  }

  const displayList = tab==='archiv' ? applyFilters(archiviert) : applyFilters(mainList);

  /* ── Detail view (full page replacement) ── */
  if (detail) {
    const v = detail.vorgang;
    const num = v ? vNum(v.number || vorgaenge.findIndex(x=>x.id===v.id)+1) : vNum((vorgaenge.length||0)+1);
    return (
      <VorgangDetail
        vorgang={v || null}
        todos={v ? (v.vorgang_todos||[]).sort((a,b)=>a.sort_order-b.sort_order) : (detail.template?.todos||[]).map((t,i)=>({id:String(i),title:t,done:false}))}
        vNum={num}
        profile={profile}
        onClose={() => setDetail(null)}
        onSaved={handleSaved}
        isNew={!v}
        template={detail.template||null}
      />
    );
  }

  /* ── Skeleton ── */
  if (loading) return (
    <div style={{ maxWidth:960, fontFamily:T.font }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:28 }}>
        <Sk w={140} h={28} r={8} /><Sk w={140} h={36} r={8} />
      </div>
      <div style={{ display:'grid', gridTemplateColumns:isMobile?'1fr':'repeat(3,1fr)', gap:14, marginBottom:24 }}>
        {[0,1,2].map(i=><Sk key={i} h={90} r={12}/>)}
      </div>
      <Sk h={42} r={8} />
    </div>
  );

  const MAIN_TABS = [
    { key:'vorgaenge', label:'Vorgänge' },
    { key:'archiv',   label:`Archiv${archiviert.length>0?' ('+archiviert.length+')':''}` },
    { key:'vorlagen', label:'Vorlagen' },
    { key:'labels',   label:'Labels' },
  ];

  return (
    <div style={{ maxWidth:960, fontFamily:T.font, color:T.ink }}>

      {/* ── Header ── */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20, gap:12, flexWrap:'wrap' }}>
        <h1 style={{ margin:0, fontSize:isMobile?22:28, fontWeight:800, letterSpacing:'-0.6px' }}>Vorgänge</h1>
        <button
          onClick={() => setTemplateModal(true)}
          style={{
            display:'flex', alignItems:'center', gap:6,
            background:T.ink, color:'white', border:'none', borderRadius:10,
            padding:'10px 18px', fontSize:13, fontWeight:700, cursor:'pointer', fontFamily:T.font,
             transition:'background 0.15s, transform 0.1s',
          }}
          onMouseEnter={e=>{e.currentTarget.style.background='#000';e.currentTarget.style.transform='translateY(-1px)';}}
          onMouseLeave={e=>{e.currentTarget.style.background=T.ink;e.currentTarget.style.transform='translateY(0)';}}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          Neuer Vorgang
        </button>
      </div>

      {/* ── KPI Cards ── */}
      <div style={{ display:'grid', gridTemplateColumns:isMobile?'1fr':'repeat(3,1fr)', gap:14, marginBottom:20 }}>
        <div style={{ background:aktive.length>0?'#f5f5f5':'white', border:`1.5px solid ${aktive.length>0?T.ink:T.border}`, borderRadius:13, padding:'18px 22px', boxShadow:'0 2px 8px rgba(0,0,0,0.04)' }}>
          <div style={{ fontSize:32, fontWeight:900, color:aktive.length>0?T.ink:T.muted, letterSpacing:'-1px', lineHeight:1 }}>{aktive.length}</div>
          <div style={{ fontSize:13, color:T.muted, marginTop:5, fontWeight:500 }}>Offene Vorgänge</div>
        </div>
        <div style={{ background:ueberfaellig.length>0?'#fff7f7':'white', border:`1.5px solid ${ueberfaellig.length>0?'#fca5a5':T.border}`, borderRadius:13, padding:'18px 22px', boxShadow:'0 2px 8px rgba(0,0,0,0.04)' }}>
          <div style={{ fontSize:32, fontWeight:900, color:ueberfaellig.length>0?'#dc2626':T.muted, letterSpacing:'-1px', lineHeight:1 }}>{ueberfaellig.length}</div>
          <div style={{ fontSize:13, color:T.muted, marginTop:5, fontWeight:500 }}>Überfällige Vorgänge</div>
        </div>
        <div style={{ background:faelligHeute.length>0?'#fffbeb':'white', border:`1.5px solid ${faelligHeute.length>0?'#fbbf24':T.border}`, borderRadius:13, padding:'18px 22px', boxShadow:'0 2px 8px rgba(0,0,0,0.04)' }}>
          <div style={{ fontSize:32, fontWeight:900, color:faelligHeute.length>0?'#d97706':T.muted, letterSpacing:'-1px', lineHeight:1 }}>{faelligHeute.length}</div>
          <div style={{ fontSize:13, color:T.muted, marginTop:5, fontWeight:500 }}>Fällige To-Dos</div>
        </div>
      </div>

      {/* ── Tabs ── */}
      <div style={{ display:'flex', borderBottom:`1px solid ${T.border}`, marginBottom:16 }}>
        {MAIN_TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{
            padding:'10px 16px', border:'none', background:'none', cursor:'pointer',
            fontSize:13, fontWeight:tab===t.key?700:400, color:tab===t.key?T.ink:T.muted,
            fontFamily:T.font, borderBottom:`2px solid ${tab===t.key?T.ink:'transparent'}`, marginBottom:-1,
          }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Vorlagen tab ── */}
      {tab==='vorlagen' && (
        <div style={{ display:'grid', gridTemplateColumns:isMobile?'1fr':'repeat(2,1fr)', gap:10 }}>
          {VORLAGEN.map(v => (
            <div key={v.id}
              onClick={() => { setTemplateModal(false); setDetail({ template:v }); }}
              style={{ background:'white', border:`1.5px solid ${T.border}`, borderRadius:12, padding:'14px 16px', cursor:'pointer', transition:'border-color 0.15s, box-shadow 0.15s' }}
              onMouseEnter={e=>{e.currentTarget.style.borderColor=T.ink;e.currentTarget.style.boxShadow='none';}}
              onMouseLeave={e=>{e.currentTarget.style.borderColor=T.border;e.currentTarget.style.boxShadow='none';}}
            >
              <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.ink} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>
                <span style={{ fontSize:13, fontWeight:600, color:T.ink }}>{v.title}</span>
              </div>
              <div style={{ fontSize:11, color:T.muted, lineHeight:1.5, paddingLeft:22 }}>{v.todos.length} To-Dos</div>
            </div>
          ))}
        </div>
      )}

      {/* ── Labels tab ── */}
      {tab==='labels' && (
        <div style={{ background:'white', border:`1.5px solid ${T.border}`, borderRadius:12, padding:'48px 24px', textAlign:'center' }}>
          <div style={{ fontSize:13, color:T.muted }}>Labels können Vorgängen beim Erstellen oder Bearbeiten zugewiesen werden.</div>
        </div>
      )}

      {/* ── Vorgänge / Archiv tab ── */}
      {(tab==='vorgaenge'||tab==='archiv') && (
        <>
          {/* Search + Filter bar */}
          <div style={{ display:'flex', gap:8, marginBottom:12, alignItems:'center', flexWrap:'wrap' }}>
            <div style={{ position:'relative', flex:1, minWidth:200 }}>
              <svg style={{ position:'absolute', left:12, top:'50%', transform:'translateY(-50%)', pointerEvents:'none' }} width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={T.muted} strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
              <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Vorgänge durchsuchen…"
                style={{ width:'100%', boxSizing:'border-box', border:`1.5px solid ${T.border}`, borderRadius:10, padding:'10px 14px 10px 36px', fontSize:14, fontFamily:T.font, color:T.ink, outline:'none' }}
                onFocus={e=>e.target.style.borderColor=T.ink} onBlur={e=>e.target.style.borderColor=T.border}
              />
            </div>

            {/* Status filter chips */}
            {STATUS_OPTS.filter(s=>s!=='archiviert').map(s=>(
              <button key={s} onClick={()=>setStatusFilter(f=>f===s?null:s)} style={{
                padding:'6px 12px', borderRadius:99, fontSize:12, fontWeight:600, cursor:'pointer', fontFamily:T.font, border:`1.5px solid ${statusFilter===s?T.ink:T.border}`,
                background:statusFilter===s?'#f5f5f5':'white', color:statusFilter===s?T.ink:T.muted,
              }}>
                {STATUS_LABEL[s]}
              </button>
            ))}

            {(statusFilter||search) && (
              <button onClick={()=>{setStatusFilter(null);setSearch('');}} style={{ background:'none', border:'none', cursor:'pointer', fontSize:12, color:T.ink, fontWeight:600, fontFamily:T.font, padding:'6px 4px' }}>
                Filter zurücksetzen
              </button>
            )}
          </div>

          {/* List */}
          <div style={{ background:'white', border:`1.5px solid ${T.border}`, borderRadius:13, overflow:'hidden', boxShadow:'0 2px 8px rgba(0,0,0,0.04)' }}>
            {displayList.length===0 ? (
              search||statusFilter ? (
                <div style={{ textAlign:'center', padding:'60px 24px' }}>
                  <div style={{ width:56, height:56, background:T.bg, borderRadius:14, display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 16px' }}>
                    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke={T.muted} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                  </div>
                  <div style={{ fontSize:16, fontWeight:700, color:T.ink, marginBottom:8 }}>Keine Vorgänge gefunden</div>
                  <div style={{ fontSize:13, color:T.muted, lineHeight:1.7 }}>Versuche andere Filter oder setze die Suche zurück.</div>
                </div>
              ) : (
                <div style={{ padding:'48px 40px' }}>
                  {/* Top section: icon + text */}
                  <div style={{ display:'flex', alignItems:'flex-start', gap:32, marginBottom:40 }}>
                    <div style={{ flexShrink:0, width:64, height:64, background:'#f7f7f7', borderRadius:16, display:'flex', alignItems:'center', justifyContent:'center', border:`1.5px solid ${T.border}` }}>
                      <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke={T.ink} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
                    </div>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:20, fontWeight:700, color:T.ink, marginBottom:8, letterSpacing:'-0.3px' }}>Noch keine Vorgänge vorhanden</div>
                      <div style={{ fontSize:14, color:T.muted, lineHeight:1.8, maxWidth:520 }}>
                        Vorgänge helfen dir, Aufgaben rund um deine Immobilien zu strukturieren und zu verfolgen — von Reparaturen über Mieterwechsel bis hin zu rechtlichen Schritten.
                      </div>
                      <button onClick={()=>setTemplateModal(true)} className="btn-primary" style={{ marginTop:20 }}>
                        + Ersten Vorgang erstellen
                      </button>
                    </div>
                  </div>
                  {/* Feature highlights */}
                  <div style={{ display:'grid', gridTemplateColumns:isMobile?'1fr':'repeat(3,1fr)', gap:16 }}>
                    {[
                      { icon:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={T.ink} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>, title:'Aufgaben & To-Dos', text:'Unterteile Vorgänge in einzelne Schritte und hake sie ab.' },
                      { icon:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={T.ink} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>, title:'Fristen & Prioritäten', text:'Setze Fälligkeitsdaten und priorisiere deine Vorgänge.' },
                      { icon:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={T.ink} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>, title:'Vorlagen nutzen', text:'Starte direkt mit fertigen Vorlagen für typische Fälle.' },
                    ].map((f,i)=>(
                      <div key={i} style={{ background:T.bg, borderRadius:12, padding:'18px 20px', border:`1px solid ${T.border}` }}>
                        <div style={{ width:36, height:36, background:'white', borderRadius:9, display:'flex', alignItems:'center', justifyContent:'center', marginBottom:12, border:`1px solid ${T.border}`, boxShadow:'0 1px 4px rgba(0,0,0,0.05)' }}>{f.icon}</div>
                        <div style={{ fontSize:13, fontWeight:700, color:T.ink, marginBottom:4 }}>{f.title}</div>
                        <div style={{ fontSize:12, color:T.muted, lineHeight:1.6 }}>{f.text}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )
            ) : displayList.map((v, i) => {
              const todos = v.vorgang_todos || [];
              const doneTodos = todos.filter(t=>t.done).length;
              const overdue = v.due_date && isPast(v.due_date) && (v.status==='offen'||v.status==='in_bearbeitung');
              return (
                <div key={v.id}
                  style={{ display:'flex', alignItems:'center', gap:14, padding:isMobile?'13px 14px':'14px 20px', borderBottom:i<displayList.length-1?`1px solid ${T.border}`:'none', cursor:'pointer', transition:'background 0.1s' }}
                  onMouseEnter={e=>e.currentTarget.style.background=T.bg}
                  onMouseLeave={e=>e.currentTarget.style.background='white'}
                  onClick={()=>setDetail({ vorgang:v })}
                >
                  <div style={{ width:8, height:8, borderRadius:'50%', background:PRIORITY_DOT[v.priority]||T.muted, flexShrink:0 }} />
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap', marginBottom:3 }}>
                      <span style={{ fontSize:11, fontWeight:700, color:T.muted }}>{vNum(v.number||i+1)}</span>
                      <span style={{ fontSize:14, fontWeight:700, color:T.ink, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{v.title}</span>
                      <span style={{ fontSize:11, fontWeight:700, borderRadius:99, padding:'2px 8px', ...STATUS_STYLE[v.status] }}>{STATUS_LABEL[v.status]}</span>
                    </div>
                    <div style={{ display:'flex', gap:12, flexWrap:'wrap' }}>
                      {todos.length>0 && (
                        <span style={{ fontSize:11, color:T.muted }}>{doneTodos}/{todos.length} To-Dos</span>
                      )}
                      {v.due_date && (
                        <span style={{ fontSize:11, fontWeight:overdue?700:400, color:overdue?'#dc2626':T.muted }}>
                          {overdue?'⚠ ':''}Fällig: {fmtDate(v.due_date)}
                        </span>
                      )}
                    </div>
                  </div>
                  <button onClick={e=>{e.stopPropagation();handleDelete(v.id);}} style={{ background:'none', border:`1px solid ${T.border}`, borderRadius:7, padding:'5px 10px', cursor:'pointer', fontSize:11, color:T.muted, fontFamily:T.font, flexShrink:0 }}
                    onMouseEnter={e=>{e.currentTarget.style.borderColor='#fca5a5';e.currentTarget.style.color='#dc2626';}}
                    onMouseLeave={e=>{e.currentTarget.style.borderColor=T.border;e.currentTarget.style.color=T.muted;}}
                  >
                    Löschen
                  </button>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* ── Template Modal ── */}
      {templateModal && (
        <TemplateModal
          onClose={()=>setTemplateModal(false)}
          onSelect={template=>{ setTemplateModal(false); setDetail({ template }); }}
          onWithout={()=>{ setTemplateModal(false); setDetail({ template:null }); }}
        />
      )}
    </div>
  );
}
